<template>
  <div class="cart-drawer critical-component-hide">
    <h3 class="text-body-m-medium cart-drawer__title">
      {{ $string('cart.drawer_title') }} ({{ visibleItemCount }})
    </h3>

    <progress-indicator
      v-if="gwp.enabled && typeof gwp.threshold === 'number'"
      v-show="cart.item_count > 0"
      class="cart-drawer__progress"
      :threshold="gwp.threshold"
      :total="cartTotalExcludingGift"
      :is-special-condition="isGiftInCart"
      :threshold-unmet-message="$string('cart.free_gift.threshold_unmet')"
      :threshold-met-message="$string('cart.free_gift.threshold_met')"
      :special-condition-message="$string('cart.free_gift.added_to_cart')"
      @threshold-met="handleThresholdMet"
    />

    <form
      v-if="cart.item_count > 0 || cartStatus.label === 'adding'"
      class="cart-drawer__form"
      :action="`${$variable('routes.cart.url')}?locale=${$variable('store.language')}`"
      method="post"
    >
      <div
        class="cart-drawer__line-items"
        js-overlay="scrollElement"
      >
        <template
          v-for="(lineItem, index) of cartItems"
          :key="getCartItemKey(lineItem, index)"
        >
          <line-item
            class="cart-drawer__line-item"
            :is-drawer-open="overlay.isActive"
            :line-item="lineItem"
            :gift-added="gwp.added"
          />

          <divider-line v-if="index !== cartItems.length - 1" />
        </template>

        <template v-if="gwp.enabled && gwp.isThresholdMet && !isGiftInCart">
          <divider-line />
          <gift-with-purchase :product="gwp.product" />
        </template>

        <transition name="fade">
          <div
            v-if="overlay.isActive"
            class="cart-drawer__widgets"
          >
            <rebuy-cart-upsell
              v-for="block of rebuyWidgets"
              :key="block.id"
              :widget-id="block.settings.id"
            />
          </div>
        </transition>
      </div>

      <footer
        v-show="cart.item_count > 0"
        class="cart-drawer__footer"
      >
        <cart-gift-note source="cart_drawer" />
        <cart-summary>
          <template #express-checkout-alt>
            <slot name="express-checkout" />
          </template>
        </cart-summary>
      </footer>
    </form>

    <div
      v-else
      class="cart-drawer__empty"
    >
      <div class="cart-drawer__empty-wrapper">
        <p
          class="text-body-m-regular text-body-l-regular-desktop"
          v-text="$string('cart.empty.drawer_text')"
        />
        <btn
          v-if="liquid.continueShopping"
          :label="$string('cart.continue_shopping')"
          :url="liquid.continueShopping"
          class="mt-m mt-l-desktop"
        />
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Vue: Cart drawer (cart-drawer)
 * -----------------------------------------------------------------------------
 * Displays contents of cart.
 *
 * @param {Object} overlay - Overlay API.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import CartGiftNote from '~async/cart-gift-note/cart-gift-note'
import CartSummary from '~async/cart-summary/cart-summary'
import GiftWithPurchase from '~async/gift-with-purchase/gift-with-purchase'
import LineItem from '~async/line-item/line-item'
import ProgressIndicator from '~async/progress-indicator/progress-indicator'
import RebuyCartUpsell from '~async/rebuy-cart-upsell/rebuy-cart-upsell'
import DividerLine from '~async/utils/divider-line/divider-line'

import Btn from '~global/btn/btn'

import { withoutUpsellItems } from '~/helpers/upsell'

export default {
  name: 'CartDrawer',

  components: {
    Btn,
    CartSummary,
    CartGiftNote,
    DividerLine,
    GiftWithPurchase,
    LineItem,
    ProgressIndicator,
    RebuyCartUpsell,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        continueShopping: '',
        gwp: {
          product: false,
          threshold: 0,
          enabled: false,
          isThresholdMet: false,
        },
        blocks: [],
      }),
    },
    overlay: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      gwp: {
        added: false,
        ...this.liquid.gwp,
      },
    }
  },

  computed: {
    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      cartStatus: (state) => state.cart.status,
    }),

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      giftLineItem: 'cart/getGiftLineItem',
      cartTotalExcludingGift: 'cart/getCartTotalExcludingGift',
      visibleItemCount: 'cart/getVisibleItemCount',
    }),

    /**
     * Compute cart items.
     * - Upsell-only items are excluded, they're displayed inside their parent.
     * - For each item that is being added, check if it's already in the cart.
     * - Check against key (if adding from cart) or id (if adding from other).
     * - If it isn't then add a false item to act as a loading placeholder.
     * @returns {Array}
     */
    cartItems() {
      const items = withoutUpsellItems(this.cart.items)

      if (this.cartStatus.label !== 'adding') {
        return items
      }

      const placeholders = []

      this.cartStatus.items.forEach((addedItem) => {
        const match = this.cart.items.find((cartItem) => {
          const cartItemProperty = typeof addedItem === 'string' ? cartItem.key : cartItem.id

          return cartItemProperty === addedItem
        })

        if (!match) {
          placeholders.push(false)
        }
      })

      return [...items, ...placeholders]
    },

    /**
     * Compute if gift item is in cart.
     * @returns {Boolean}
     */
    isGiftInCart() {
      return Boolean(this.giftLineItem)
    },

    /**
     * Compute rebuy widget blocks
     * @returns {Array}
     */
    rebuyWidgets() {
      return this.liquid.blocks.filter((block) => block.type === 'rebuy_widget')
    },
  },

  watch: {
    /**
     * Watch cart items to update focus trap.
     */
    'cart.items'() {
      if (!this.overlay.isActive) {
        return
      }

      this.overlay.update({ currentElement: document.activeElement })
    },

    /**
     * Watch gift line item getter to set gift just added message
     */
    giftLineItem(newItem, oldItem) {
      if (oldItem || !newItem) {
        return
      }

      this.gwp.added = true

      setTimeout(() => {
        this.gwp.added = false
      }, 5 * 1000)
    },

    'overlay.isActive'() {
      if (this.overlay.isActive) {
        this.setCartResponse({
          publishAnalytics: false,
          syncManagedProperties: true,
          updateHistory: false,
          updateStatus: false,
        })
        this.overlay.update({ currentElement: document.activeElement })
      }
    },
  },

  mounted() {
    if (!this.overlay.isActive) {
      return
    }
    this.setCartResponse({
      publishAnalytics: false,
      syncManagedProperties: true,
      updateHistory: false,
      updateStatus: false,
    })
    this.overlay.update()
  },

  methods: {
    /**
     * Map Vuex actions.
     */
    ...mapActions({
      removeItem: 'cart/updateItem',
      setCartResponse: 'cart/setResponse',
    }),

    /**
     * Get cart item key.
     * @param {Object} item - Line item.
     * @param {Number} index - Line item index.
     * @returns {String}
     */
    getCartItemKey(item, index) {
      if (!item) {
        return `cart-line-item-loading-${index}`
      }

      return `cart-line-item-${item.key}`
    },

    /**
     * Shopify section editor select event.
     */
    shopifySectionSelect() {
      this.overlay.open({
        component: 'cart-drawer',
        ignoreDismissed: true,
      })
    },

    /**
     * Shopify section editor deselect event.
     */
    shopifySectionDeselect() {
      this.overlay.close()
    },

    /**
     * Handle threshold met event.
     * @param {Boolean} isMet - Is threshold met.
     * @returns {Boolean}
     */
    handleThresholdMet(isMet) {
      this.gwp.isThresholdMet = isMet
      if (!isMet) {
        this.handleRemoveGiftItem()
      }
    },

    /**
     * Handle remove gift item.
     */
    handleRemoveGiftItem() {
      if (!this.giftLineItem) {
        return
      }
      this.removeItem(this.giftLineItem.key)
    },
  },
}
</script>

<style lang="scss">
@import './cart-drawer';
</style>
